import styled from "styled-components/macro";
import { colors } from "src/theme/colors";
import { line, radii, withAlpha } from "src/theme/ui";

/**
 * One of the ways to add a bank account.
 *
 * What this was: a 566x376 div with a 2px #e8f1ff border on a 15px corner,
 * 84px of side padding, everything inside it centred, a title in bold
 * MERRIWEATHER — the marketing site's serif, which DESIGN.md keeps off every
 * product screen — coloured with the accent, body copy at 12px in pure black,
 * and a shadow that appeared on hover. A card in this product has a hairline
 * and no shadow ever; a shadow here means the thing can be dismissed.
 *
 * And it was a div with an onClick. So the two choices on this panel could
 * not be reached by keyboard at all, had no role, and did not answer the
 * space bar: a customer navigating without a mouse could not add a bank
 * account. It is a button now, which is what it always was.
 */
export const Container = styled.button<{ $fixedSize?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  max-width: ${({ $fixedSize }) => ($fixedSize ? "560px" : "none")};
  margin: 0;
  padding: 18px 20px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.lg}px;
  background: ${colors.white};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease;

  &:hover:not(:disabled) {
    border-color: ${colors.gray[300]};
    background: ${colors.gray[25]};
  }

  &:focus-visible {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }

  /* It keeps its border: a choice that drops its box stops looking like a
     choice. What it loses is its ink, and what it gains is a line saying
     why — see the Reason below. */
  &:disabled {
    background: ${colors.gray[50]};
    cursor: not-allowed;
  }
`;

/* The glyph at full strength on a tint of its own hue, at the size every
   other row's mark in this product uses. The two icons here were pale
   grey-blue line drawings from a second icon family, large enough to be the
   loudest thing in the card and faint enough to be unreadable. */
export const Disc = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: ${radii.control}px;
  background: ${withAlpha(colors.primary[500], 0.1)};
  color: ${colors.primary[600]};

  ${Container}:disabled & {
    background: ${colors.gray[100]};
    color: ${colors.gray[400]};
  }
`;

export const Body = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

/* Ink, at the product's own title size. Not the accent: the accent marks
   what a reader can act on, and the whole card is already that. */
export const Title = styled.span`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: ${colors.gray[900]};

  ${Container}:disabled & {
    color: ${colors.gray[500]};
  }
`;

export const Text = styled.span`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${colors.gray[500]};

  ${Container}:disabled & {
    color: ${colors.gray[400]};
  }
`;

/**
 * Why a choice cannot be taken.
 *
 * The automated option was switched off by wrapping it in a div carrying
 * `opacity: 0.5; pointer-events: none` inline, on every page except the
 * deposit one — so in Settings a customer met a faded card offering
 * "Automated Transfer (UAE banks)" and was told nothing at all about why it
 * would not respond. A disabled control has to say what would make it
 * available.
 */
export const Reason = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.35;
  color: ${colors.warning[700]};
`;
